import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { RootState } from 'app/store/rootReducer';
import type { V2TransactionFilterOptionsPayload } from 'CDK_API';
import { transactionService } from 'app/services/transaction.service';

/**
 * Fetch transaction filter options from cached table using custom Lambda resolver.
 * This provides available filter values for the advanced filter panel.
 *
 * SEE-301: served by the CDK AppSync API. `null` means the cache row does not exist
 * yet — a legitimate empty result. Anything that throws (including an
 * `assert_paidol_member` denial, which arrives as a generic GraphQL error and cannot
 * be distinguished from a real backend failure) must reject rather than collapse
 * into that same `null`.
 */
export const fetchTransactionFilterOptions = createAsyncThunk<
  V2TransactionFilterOptionsPayload | null,
  { paidolId: string }
>('transactionFilterOptions/fetch', async ({ paidolId }, { rejectWithValue }) => {
  try {
    return await transactionService.v2GetTransactionFilterOptions({ paidolId });
  } catch (error) {
    return rejectWithValue(error);
  }
});

const initialState = {
  data: null as V2TransactionFilterOptionsPayload | null,
  loading: false,
  error: null as string | null,
};

export type TransactionFilterOptionsState = typeof initialState;

const transactionFilterOptionsSlice = createSlice({
  name: 'transactionFilterOptions',
  initialState,
  reducers: {
    clearFilterOptions: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(fetchTransactionFilterOptions.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchTransactionFilterOptions.fulfilled, (state, action) => {
      state.data = action.payload;
      state.loading = false;
      state.error = null;
    });
    builder.addCase(fetchTransactionFilterOptions.rejected, (state, action) => {
      state.loading = false;
      // Handle both GraphQL errors (array) and regular errors
      const payload = action.payload as any;
      state.error =
        (Array.isArray(payload) ? payload[0]?.message : payload?.message) ||
        action.error.message ||
        'Failed to load filter options';
    });
  },
});

export const { clearFilterOptions } = transactionFilterOptionsSlice.actions;

export const selectTransactionFilterOptions = (state: RootState): TransactionFilterOptionsState =>
  state.cards?.transactionFilterOptions ?? initialState;

export default transactionFilterOptionsSlice.reducer;
